import PageRecord from "models/view-models/page-record";
import useCurrentIdentity from "utilities/hooks/use-current-identity";
import { CoreUtils } from "@rsm-hcd/javascript-core";
import { useEffect, useMemo, useState } from "react";
import useNotebooksAtom from "utilities/hooks/domain/notebooks/use-notebooks-atom";
import { NotebookConstants } from "constants/notebook-constants";

export enum PageAction {
    Open = 0,
    Print = 1,
    Delete = 2,
    Rename = 3,
    Clone = 4,
    Share = 5,
    ManageRecipientAccess = 6,
    Relocate = 7,
    Edit = 8,
    ManageCustomLogo = 9,
}
const ALL_ACTIONS = CoreUtils.enumToArray(PageAction);
const BASE_ACTIONS = [PageAction.Open, PageAction.Print];
const BASE_PAGE_IN_SHARED_NOTEBOOK_PERMISSIONS = [
    ...BASE_ACTIONS,
    PageAction.Clone,
];

const usePageActionPermission = (page: PageRecord | null) => {
    const { currentIdentity } = useCurrentIdentity();
    const { resultObject: notebooks } = useNotebooksAtom();
    const [permissions, setPermissions] = useState<PageAction[]>([]);

    const notebookForPage = useMemo(() => {
        return notebooks.find((notebook) => notebook.id === page?.notebookId);
    }, [notebooks, page?.notebookId]);

    const isSharedNotebook =
        notebookForPage?.ownerId !== currentIdentity?.userId() &&
        notebookForPage?.id !== NotebookConstants.SharedPageNotebookId;

    useEffect(() => {
        if (!page) {
            setPermissions([]);
            return;
        }

        if (page.ownerId === currentIdentity?.userId()) {
            setPermissions(ALL_ACTIONS);
            return;
        }

        // TODO: Refactor as part of LINK-2072
        if (isSharedNotebook) {
            setPermissions(BASE_PAGE_IN_SHARED_NOTEBOOK_PERMISSIONS);
            return;
        }

        // Build permission based on page and user data.
        const allowedActions: PageAction[] = [...BASE_ACTIONS];

        if (page.isCloningPermitted || isSharedNotebook) {
            allowedActions.push(PageAction.Clone);
        }

        setPermissions(allowedActions);
    }, [currentIdentity, isSharedNotebook, page]);

    const can = (action: PageAction) => {
        return permissions.includes(action);
    };
    const hasOnlyBasePermissions = () => {
        return (
            permissions.length === BASE_ACTIONS.length &&
            BASE_ACTIONS.every((action) => permissions.includes(action))
        );
    };

    return {
        permissions,
        can,
        hasOnlyBasePermissions,
    };
};

export default usePageActionPermission;
